import { ChevronDown } from "lucide-react";
import React, { FC, ReactNode } from "react";

import {
  IgBorderColors,
  IgCoreBackgroundColors,
  IgShadows,
} from "@intelligems/ig-design-tokens";
import {
  Accordion,
  AccordionDetailsProps,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  Stack,
  useTheme,
} from "@mui/material";
import AccordionDetails from "@mui/material/AccordionDetails";
import Grid from "@mui/material/Grid";

import { IgChip } from "./IgChip";
import { IgTypography, IgTypographyProps } from "./IgTypography";

// Using intersection instead of extends Omit<> to reduce tsc type comparison cost (~530ms saved)
type IgAccordionBaseProps = {
  title: ReactNode;
  titleVariant?: IgTypographyProps["variant"];
  description?: string;
  customDescription?: ReactNode;
  headerIcon?: ReactNode;
  endHeaderIcon?: ReactNode;
  summaryActionButtons?: ReactNode[];
  hideSummaryActionButtons?: boolean;
  showOptionalChip?: boolean;
  accordionSummaryProps?: AccordionSummaryProps;
  accordionDetailsProps?: AccordionDetailsProps;
};

type IgAccordionProps = IgAccordionBaseProps & Omit<AccordionProps, "title">;

/**
 * @see Grid
 */
export const IgAccordion: FC<IgAccordionProps> = (props) => {
  const {
    title,
    titleVariant = "h3",
    description,
    summaryActionButtons,
    accordionSummaryProps,
    accordionDetailsProps,
    hideSummaryActionButtons,
    showOptionalChip,
    children,
    headerIcon,
    customDescription,
    endHeaderIcon,
    sx,
    ...rest
  } = props;
  const theme = useTheme();

  return (
    <Accordion
      sx={[
        {
          width: "100%",
          backgroundColor: IgCoreBackgroundColors.baseLightHigh,
          border: `1px solid ${IgBorderColors.core.default} !important`,
          padding:
            description || customDescription
              ? `12px 16px 16px 16px`
              : "4px 16px",
          "&::before": {
            display: "none",
          },
          "&:hover": {
            borderShadow: `${IgShadows.low} !important`,
            border: `1px solid ${IgBorderColors.core.hover} !important`,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      <AccordionSummary
        sx={{
          paddingLeft: "0px",
          paddingRight: "0px",
          "& .MuiAccordionSummary-content": {
            margin: "0px 0px",
          },
        }}
        expandIcon={
          hideSummaryActionButtons ? null : (
            <ChevronDown color={theme.igIconColors.light} size={16} />
          )
        }
        component={"div"}
      >
        <Grid
          container
          direction={"row"}
          flexWrap={"nowrap"}
          alignItems={"center"}
          justifyContent={"space-between"}
          wrap={"nowrap"}
          width={"100%"}
        >
          <Stack direction={"column"}>
            <Grid
              container
              direction={"row"}
              flexWrap={"nowrap"}
              wrap={"nowrap"}
              columnSpacing={1}
              alignItems={"center"}
              size={12}
            >
              {headerIcon && <Grid>{headerIcon}</Grid>}
              <Grid>
                <IgTypography variant={titleVariant}>{title}</IgTypography>
              </Grid>
              {endHeaderIcon && <Grid>{endHeaderIcon}</Grid>}
              {showOptionalChip && (
                <Grid>
                  <IgChip
                    disabled={true}
                    clickable={false}
                    sx={{
                      border: "1px dashed black",
                    }}
                    size={"small"}
                    label={`Optional`}
                  />
                </Grid>
              )}
            </Grid>
            {description || customDescription ? (
              <Grid>
                {customDescription ? (
                  customDescription
                ) : description ? (
                  <IgTypography
                    variant={"body2"}
                    color={theme.palette.text.secondary}
                  >
                    {description}
                  </IgTypography>
                ) : null}
              </Grid>
            ) : null}
          </Stack>
          {summaryActionButtons && (
            <Grid
              container
              justifyContent={"flex-end"}
              direction={"row"}
              mr={3}
              alignItems={"center"}
              columnSpacing={1}
              className={"summary-action-buttons"}
            >
              {summaryActionButtons.map((button, index) => (
                <Grid key={index} columnSpacing={1}>
                  {button}
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          paddingLeft: "0px",
          paddingRight: "0px",
          marginTop: "4px",
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
